/**
 * The hierarchy of components here is:
 * JobDrawer - thin wrapper to allow animated drawer transition without loading content
 * JobDrawerContent - queries for data, shows loading indicator
 * JobDrawerLoaded - UI the user interacts with
 */

import {Drawer} from '@mui/material';
import {omit} from 'lodash';
import React, {useState} from 'react';

import {ResizableDrawer} from '../../common/ResizableDrawer/ResizableDrawer';
import * as S from './JobDrawer.styles';
import {
  JobDrawerContent,
  JobDrawerContentLocked,
  type JobDrawerContentLockedProps,
  type JobDrawerContentProps,
} from './JobDrawerContent';
import {JobDrawerStateProvider} from './JobDrawerStateContext';

type JobDrawerOuterProps = {
  open: boolean;
  closeDrawer: () => void;
  children: React.ReactNode;
  variant: 'persistent' | 'temporary';
};

const JobDrawerOuter = (props: JobDrawerOuterProps) => {
  const {children, open, variant, closeDrawer} = props;
  const [width, setWidth] = useState(500); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  return (
    <Drawer anchor="right" variant={variant} open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <S.DrawerContents>{children}</S.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};

export type JobDrawerProps = JobDrawerContentProps & {
  open: boolean;
  variant?: 'persistent' | 'temporary';
};

// We instantiate the drawer even when there is no job id selected
// so that it can animate sliding in when one is selected.
export const JobDrawer = (props: JobDrawerProps) => {
  const {children, open, variant, closeDrawer} = props;
  return (
    <JobDrawerStateProvider>
      <JobDrawerOuter
        open={open}
        variant={variant != null ? variant : 'temporary'}
        closeDrawer={closeDrawer}>
        <JobDrawerContent {...omit(props, ['children'])}>
          {children}
        </JobDrawerContent>
      </JobDrawerOuter>
    </JobDrawerStateProvider>
  );
};

type JobDrawerLockedProps = JobDrawerContentLockedProps & {
  open: boolean;
};

export const JobDrawerLocked = (props: JobDrawerLockedProps) => {
  const {children, open, closeDrawer, defaultRunToClone} = props;

  return (
    <JobDrawerStateProvider rerunFromRunName={defaultRunToClone}>
      <JobDrawerOuter open={open} variant="temporary" closeDrawer={closeDrawer}>
        <JobDrawerContentLocked {...omit(props, ['children'])}>
          {children}
        </JobDrawerContentLocked>
      </JobDrawerOuter>
    </JobDrawerStateProvider>
  );
};
